/**
 * MODULE: _shared/lib/frontend/api/config.ts
 * PURPOSE: API configuration and authentication helpers - centralized config for all API calls via API Gateway with JWT token management
 * 
 * DEPENDENCIES:
 *  - Vite environment variables (import.meta.env)
 *  - Browser localStorage API
 * 
 * PUBLIC API:
 *  - getApiBaseUrl(): Resolve API gateway base URL (dev vs prod runtime injection)
 *  - API_CONFIG: { baseURL, timeout, headers } - Base configuration object
 *  - getAuthToken(): Resolve JWT from localStorage (and dev overrides)
 *  - readJwtExpSeconds() / isAccessTokenExpired(): JWT expiry helpers for streaming preflight
 *  - getTenantId(): Get tenant ID from localStorage (tenant_id or tenantId)
 *  - getAuthHeader(): Create { Authorization: "Bearer <token>" } header object
 *  - getAuthHeaders(): Get headers with Authorization, Content-Type, and X-Tenant-ID (for JSON body)
 *  - getAuthHeadersForFormData(): Auth + X-Tenant-ID without Content-Type (for FormData/multipart)
 *  - apiV1Url(path): Same-origin **`/api/v1${path}`** (or gateway base + path); strips Compose-only hosts
 * 
 * SIDE EFFECTS:
 *  - Reads runtime window.__APP_CONFIG__.apiBaseUrl (config.js + Docker entrypoint) in prod
 *  - Reads VITE_API_BASE_URL in dev / optional prod fallback
 *  - Reads tokens from localStorage (Brazn keys, Supabase `sb-*-auth-token`, `window.BRAZN_DEV_TOKEN`, optional `__BRAZN_AUTH_KEY__`)
 *  - Browser API access (window, localStorage)
 * 
 * AGENT SAFETY: SAFE
 * FILE SIZE: 427 / 450
 * Recent: 2026-06-04 [2.2.1273] — **`getAuthToken`** prefers first non-expired JWT across storage keys; **`getTenantId`** canonical **`tnt_*`** for **`X-Tenant-ID`**.
 * Recent: 2026-05-05 — **`coerceBrowserGatewayBase`**: treat **scheme-less** **`brazn-*:port`** API bases as Docker-only (browser **ERR_NAME_NOT_RESOLVED**). [2.2.818] Same-origin Vite dev ports (**`5176`–`5178`**, **`5180`**) … **`getApiBaseUrl`**: extend default Vite dev port set … [2.2.795] **`getAuthToken`**: throttle … [2.2.788] **`apiV1Url`**: Docker-host **`import.meta.env`** pool … [2.2.783] **`apiV1Url`**: normalise **`brazn-*:port/...`**. [2.2.782] **`coerceBrowserGatewayBase`**. 2026-04-19 [2.2.554] — **`getAuthToken()`** …
 * 
 * See: /README.md | /ARCHITECTURE.md | /_shared/frontend-shell/README.md
 */

/// <reference types="vite/client" />

/**
 * API Configuration
 * Centralized configuration for all API calls
 */

/** Must match placeholder in _shared/frontend-shell/public/config.js */
const RUNTIME_CONFIG_PLACEHOLDER = '__VITE_API_BASE_URL__';

function readRuntimeInjectedApiUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as Window & { __APP_CONFIG__?: { apiBaseUrl?: string } };
  const url = w.__APP_CONFIG__?.apiBaseUrl?.trim();
  if (!url || url === RUNTIME_CONFIG_PLACEHOLDER) return undefined;
  return url.replace(/\/$/, '');
}

/** True for compose/K8s service DNS names that resolve inside Docker but not in the user's browser. */
function isDockerInternalOnlyHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1') return false;
  if (h.startsWith('brazn-')) return true;
  if (!h.includes('.') && h.endsWith('-service')) return true;
  return false;
}

/** Drop misconfigured API bases so fetch/axios never targets unreachable Docker hostnames from the browser. */
function sanitizeBrowserApiBase(raw: string | undefined): string | undefined {
  if (!raw || !raw.startsWith('http')) return undefined;
  try {
    const { hostname } = new URL(raw);
    if (isDockerInternalOnlyHostname(hostname)) {
      console.warn(
        '[Config] Ignoring API base URL not reachable from the browser (Docker service hostname):',
        hostname,
        '— use http://localhost:8080, your LAN IP, or a public API host.',
      );
      return undefined;
    }
    return raw.replace(/\/$/, '');
  } catch {
    return undefined;
  }
}

/**
 * Never emit Compose-only API hosts for browser-bound URL builders.
 * When **`window` is undefined** (Vite dep scan or Node evaluating this module), we still strip
 * **`brazn-*`** bases so a bad value is not frozen at import time and later used from **`fetch`** /
 * Axios in the browser (**ERR_NAME_NOT_RESOLVED**).
 */
function coerceBrowserGatewayBase(url: string): string {
  const trimmed = (url || '').replace(/\/$/, '');
  if (!trimmed) return trimmed;
  // Compose sometimes sets API bases without a scheme (`brazn-operations-service:8010`). The
  // browser then resolves them as hostnames and XHR targets Docker DNS (**ERR_NAME_NOT_RESOLVED**).
  if (!trimmed.startsWith('http')) {
    const firstSeg = (trimmed.split('/')[0] ?? '').trim();
    if (firstSeg.includes(':') || /^brazn-/i.test(firstSeg)) {
      try {
        const host = new URL(`http://${firstSeg}`).hostname;
        if (isDockerInternalOnlyHostname(host)) {
          if (typeof window !== 'undefined') {
            console.warn(
              '[Config] Ignoring scheme-less Docker-internal API base (use same-origin `/api` or localhost gateway):',
              firstSeg,
            );
          }
          return '';
        }
      } catch {
        /* keep trimmed */
      }
    }
    return trimmed;
  }
  try {
    const { hostname } = new URL(trimmed);
    if (isDockerInternalOnlyHostname(hostname)) {
      if (typeof window !== 'undefined') {
        console.warn(
          '[Config] Browser cannot resolve Docker-internal API host; using same-origin `/api` (Vite proxy or ingress):',
          hostname,
        );
      }
      return '';
    }
  } catch {
    return trimmed;
  }
  return trimmed;
}

/** Same as **`coerceBrowserGatewayBase`** — exported for callers that build URLs outside **`getApiBaseUrl()`**. */
export function stripDockerComposeApiBase(base: string): string {
  return coerceBrowserGatewayBase(base);
}

/**
 * Turns `http://brazn-*-service:port/api/...` into `/api/...` (Compose DNS never resolves in the client).
 * Rewrites even when **`window` is undefined** so Node-time URL assembly cannot leak absolute Docker URLs.
 */
export function resolveDockerServiceUrlToSameOriginPath(url: string): string {
  if (!url || !url.startsWith('http')) return url;
  try {
    const { hostname, pathname, search } = new URL(url);
    if (isDockerInternalOnlyHostname(hostname)) {
      const path = `${pathname}${search}` || '/';
      if (typeof window !== 'undefined') {
        console.warn('[Config] Rewrote Docker-internal absolute URL to same-origin path:', hostname);
      }
      return path;
    }
  } catch {
    return url;
  }
  return url;
}

/**
 * Build the final URL for **`/api/v1${pathAfterV1}`** (Axios **`baseURL`** + relative **`url`** is easy to mis-merge).
 * `pathAfterV1` must start with **`/`** (e.g. **`/sales-opportunities`**, **`/territories`**).
 *
 * When **`VITE_API_BASE_URL`** or runtime **`__APP_CONFIG__.apiBaseUrl`** still mentions Compose-only
 * hosts (**`brazn-*`**, **`*-service`**), the browser must not build absolute URLs from them (**ERR_NAME_NOT_RESOLVED**),
 * even if another code path read **`getApiBaseUrl()`** earlier with a stale value.
 */
export function apiV1Url(pathAfterV1: string): string {
  let pathAfterV1Cleaned = pathAfterV1;
  if (
    typeof pathAfterV1Cleaned === 'string' &&
    !pathAfterV1Cleaned.startsWith('/') &&
    !pathAfterV1Cleaned.startsWith('http') &&
    /^brazn-[a-z0-9-]+:\d+/i.test(pathAfterV1Cleaned)
  ) {
    const slashIdx = pathAfterV1Cleaned.indexOf('/');
    pathAfterV1Cleaned = slashIdx >= 0 ? pathAfterV1Cleaned.slice(slashIdx) : '/';
  }
  const p = pathAfterV1Cleaned.startsWith('/') ? pathAfterV1Cleaned : `/${pathAfterV1Cleaned}`;
  const pathOnly = (`/api/v1${p}`).replace(/([^:])\/{2,}/g, '$1/');

  // `import.meta.env` is always defined in Vite bundles — do not gate on `window`. Dep-scan / SSR
  // can evaluate this module with `window` undefined; skipping the pool let a baked `brazn-*`
  // `VITE_API_BASE_URL` flow into `prefix` + absolute URLs (ERR_NAME_NOT_RESOLVED in the browser).
  const rawEnv = String(import.meta.env.VITE_API_BASE_URL ?? '');
  let injected = '';
  if (typeof window !== 'undefined') {
    try {
      const w = window as Window & { __APP_CONFIG__?: { apiBaseUrl?: string } };
      injected = String(w.__APP_CONFIG__?.apiBaseUrl ?? '');
    } catch {
      injected = '';
    }
  }
  const pool = `${rawEnv}\n${injected}`;
  if (/brazn-[a-z0-9-]+/i.test(pool) || /https?:\/\/[^\s"'<>]+-service(:\d+)?/i.test(pool)) {
    return pathOnly;
  }

  const base = stripDockerComposeApiBase(getApiBaseUrl().replace(/\/$/, ''));
  const prefix = base ? `${base}/api/v1` : '/api/v1';
  let out = resolveDockerServiceUrlToSameOriginPath(`${prefix}${p}`);
  if (out.startsWith('http')) {
    try {
      const u = new URL(out);
      const h = u.hostname.toLowerCase();
      if (h.startsWith('brazn-') || (!h.includes('.') && h.endsWith('-service'))) {
        out = `${u.pathname}${u.search}` || '/';
      }
    } catch {
      /* keep out */
    }
  }
  return out;
}

/**
 * API Gateway base URL.
 * - Dev (Vite :4173 / :5173–:5175): default '' so requests stay same-origin `/api/...` and the dev proxy adds CORS-free hops to :8080.
 * - Dev (other): VITE_API_BASE_URL if set, else hostname :8080.
 * - Prod (browser): window.__APP_CONFIG__.apiBaseUrl from config.js (injected at container start).
 */
export function getApiBaseUrl(): string {
  const buildUrlRaw = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const buildUrl = sanitizeBrowserApiBase(buildUrlRaw);

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const w = window.location;
    // Include common alternate Vite ports (extra shells, Cursor Browser, monorepo dev servers).
    const vitePorts = new Set(['4173', '5173', '5174', '5175', '5176', '5177', '5178', '5180']);
    if (vitePorts.has(w.port)) {
      if (!buildUrl) return coerceBrowserGatewayBase('');
      try {
        const u = new URL(buildUrl);
        const targetsLocalGateway =
          (u.hostname === 'localhost' || u.hostname === '127.0.0.1') &&
          (u.port === '8080' || u.port === '');
        if (targetsLocalGateway) return coerceBrowserGatewayBase('');
        return coerceBrowserGatewayBase(buildUrl.replace(/\/$/, ''));
      } catch {
        return coerceBrowserGatewayBase('');
      }
    }
    if (buildUrl) return coerceBrowserGatewayBase(buildUrl);
    const protocol = w.protocol || 'http:';
    return coerceBrowserGatewayBase(`${protocol}//${w.hostname}:8080`);
  }

  if (typeof window !== 'undefined') {
    const injected = sanitizeBrowserApiBase(readRuntimeInjectedApiUrl());
    if (injected) return coerceBrowserGatewayBase(injected);
    if (buildUrl) return coerceBrowserGatewayBase(buildUrl);
    const origin = window.location.origin.replace(/\/$/, '');
    if (origin && !isDockerInternalOnlyHostname(window.location.hostname)) {
      console.warn(
        '[Config] apiBaseUrl missing or Docker-only; using page origin as API base:',
        origin,
      );
      return coerceBrowserGatewayBase(origin);
    }
    throw new Error(
      '[Config] apiBaseUrl is not set. Did entrypoint.sh run? Expected window.__APP_CONFIG__.apiBaseUrl from /config.js.',
    );
  }

  const raw = buildUrl || 'http://localhost:8080';
  try {
    const host = new URL(raw).hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host.includes('.'))
      return raw.replace(/\/$/, '');
    return 'http://localhost:8080';
  } catch {
    return 'http://localhost:8080';
  }
}

export const API_CONFIG = {
  get baseURL() {
    return getApiBaseUrl();
  },
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
};

type BraznAuthWindow = Window & {
  BRAZN_DEV_TOKEN?: string;
  __BRAZN_AUTH_KEY__?: string;
};

/** Throttle **`getAuthToken`** "no token" warnings so parallel API calls do not flood the console. */
let lastGetAuthTokenNoTokenLogMs = 0;
const GET_AUTH_TOKEN_NO_TOKEN_LOG_INTERVAL_MS = 30_000;

/**
 * Handle both raw JWT strings (typical `eyJ…`) and JSON blobs like
 * `{"access_token":"…","expires_at":…}` (Supabase session shape).
 */
function parseTokenFromStorage(raw: string): string | null {
  if (!raw || !raw.trim()) return null;
  const trimmed = raw.trim();
  if (trimmed.startsWith('eyJ')) return trimmed;
  try {
    const parsed = JSON.parse(trimmed) as { access_token?: string };
    const at = parsed?.access_token;
    return typeof at === 'string' && at.length > 0 ? at : null;
  } catch {
    return null;
  }
}

/** JWT `exp` in seconds, or null if not a decodable three-segment JWT. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    return JSON.parse(atob(b64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function readJwtExpSeconds(token: string): number | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  return typeof exp === 'number' ? exp : null;
}

/**
 * True when JWT is malformed or expired (30s clock-skew buffer before real expiry).
 * Tokens without an `exp` claim are treated as non-expiring (returns false).
 */
export function isAccessTokenExpired(token: string): boolean {
  try {
    const payload = decodeJwtPayload(token);
    if (!payload) return true;
    const exp = payload.exp;
    if (exp == null || typeof exp !== 'number') return false;
    return exp < Date.now() / 1000 + 30;
  } catch {
    return true;
  }
}

/** Prefer a still-valid JWT; fall back to the first candidate so refresh can run on 401. */
function pickBearerToken(candidates: Array<string | null>): string | null {
  let stale: string | null = null;
  for (const t of candidates) {
    if (!t) continue;
    if (!isAccessTokenExpired(t)) return t;
    if (!stale) stale = t;
  }
  return stale;
}

/**
 * Resolve the bearer token for API and SSE (`?token=`) calls.
 * Order: dev override → explicit key → Supabase session key → common localStorage keys.
 * Skips expired entries when a later key still has a valid JWT (e.g. stale `brazn_access_token`).
 */
export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;

  const w = window as BraznAuthWindow;

  if (w.BRAZN_DEV_TOKEN) {
    return String(w.BRAZN_DEV_TOKEN).trim() || null;
  }

  const candidates: Array<string | null> = [];

  const configKey = w.__BRAZN_AUTH_KEY__;
  if (configKey) {
    const raw = localStorage.getItem(configKey);
    if (raw) candidates.push(parseTokenFromStorage(raw));
  }

  const supabaseKey = Object.keys(localStorage).find((k) => /^sb-.+-auth-token$/.test(k));
  if (supabaseKey) {
    const raw = localStorage.getItem(supabaseKey);
    if (raw) candidates.push(parseTokenFromStorage(raw));
  }

  for (const k of ['token', 'brazn_access_token', 'access_token', 'authToken', 'jwt']) {
    const raw = localStorage.getItem(k);
    if (raw) candidates.push(parseTokenFromStorage(raw));
  }

  const picked = pickBearerToken(candidates);
  if (picked) return picked;

  const now = Date.now();
  if (now - lastGetAuthTokenNoTokenLogMs >= GET_AUTH_TOKEN_NO_TOKEN_LOG_INTERVAL_MS) {
    lastGetAuthTokenNoTokenLogMs = now;
    console.warn(
      '[Brazn] getAuthToken: no token found. Auth-related keys:',
      Object.keys(localStorage).filter((key) => /auth|token|jwt|supabase|sb-/i.test(key)),
    );
  }
  return null;
}

/** Gateway tenant middleware expects JWT-shaped ids (often `tnt_<uuid>`). */
function canonicalTenantHeaderId(raw: string | null): string | null {
  if (!raw?.trim()) return null;
  const t = raw.trim();
  if (t.startsWith('tnt_')) return t;
  if (/^[0-9a-f-]{36}$/i.test(t)) return `tnt_${t}`;
  return t;
}

/** Active tenant from JWT (authoritative for gateway X-Tenant-ID). */
export function getJwtTenantId(): string | null {
  const token = getAuthToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  const raw = typeof payload?.tenant_id === 'string' ? payload.tenant_id.trim() : '';
  if (!raw) return null;
  return canonicalTenantHeaderId(raw);
}

// JWT tenant claim wins over localStorage (gateway strips client X-Tenant-ID and injects JWT tenant)
export const getTenantId = (): string | null => {
  if (typeof window !== 'undefined') {
    const fromJwt = getJwtTenantId();
    if (fromJwt) {
      const bare = fromJwt.replace(/^tnt_/, '');
      const stored = localStorage.getItem('tenant_id');
      if (stored !== bare) {
        localStorage.setItem('tenant_id', bare);
      }
      return fromJwt;
    }
    const raw = localStorage.getItem('tenant_id') || localStorage.getItem('tenantId');
    return canonicalTenantHeaderId(raw);
  }
  return null;
};

// Create authorization header
export const getAuthHeader = (): { Authorization: string } | {} => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Get auth headers with content type and tenant context (for JSON body)
export const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    ...getAuthHeader(),
    'Content-Type': 'application/json'
  };
  const tenantId = getTenantId();
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
};

/** Auth headers for FormData requests – omit Content-Type so browser sets multipart/form-data with boundary */
export const getAuthHeadersForFormData = (): Record<string, string> => {
  const headers: Record<string, string> = { ...getAuthHeader() };
  const tenantId = getTenantId();
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
};

